/**
 * Risk case management with a review queue and an appeal path (REQ-RSK-01 tail).
 *
 * A REVIEW or hard-block decision opens a case that carries the decision's reasons (explainable). A case is
 * reviewed, then confirmed or dismissed; a confirmed case can be appealed and either overturned (the false-
 * positive path) or upheld. Every transition is audited.
 */
import { err, ok } from '@vello/core';
export const RISK_CASE_STATUSES = [
    'OPEN',
    'UNDER_REVIEW',
    'CONFIRMED',
    'DISMISSED',
    'APPEALED',
    'OVERTURNED',
    'UPHELD',
];
const EDGES = {
    OPEN: ['UNDER_REVIEW', 'DISMISSED'],
    UNDER_REVIEW: ['CONFIRMED', 'DISMISSED'],
    CONFIRMED: ['APPEALED'],
    DISMISSED: [],
    APPEALED: ['OVERTURNED', 'UPHELD'],
    OVERTURNED: [],
    UPHELD: [],
};
export function canCaseTransition(from, to) {
    return EDGES[from].includes(to);
}
/** Open a case from a decision that needs human handling (REVIEW or a hard block). */
export function openCaseFromDecision(caseId, subjectId, decision, now) {
    const needsCase = decision.action === 'REVIEW' || decision.action === 'BLOCK_CASH' || decision.action === 'HOLD_PAYOUT' || decision.action === 'SUSPEND';
    if (!needsCase) {
        return err({ code: 'NOT_REVIEWABLE', message: `action ${decision.action} does not open a case` });
    }
    return ok({
        caseId,
        subjectId,
        status: 'OPEN',
        action: decision.action,
        reasons: decision.reasons,
        policyVersion: decision.policyVersion,
        createdAt: now,
        updatedAt: now,
        audit: [{ at: now, actorId: 'system', fromStatus: 'OPEN', toStatus: 'OPEN', note: 'CASE_OPENED' }],
    });
}
export function transitionCase(c, to, actorId, note, now) {
    if (!canCaseTransition(c.status, to)) {
        return err({ code: 'ILLEGAL_TRANSITION', message: `${c.status} -> ${to}` });
    }
    return ok({
        ...c,
        status: to,
        updatedAt: now,
        audit: [...c.audit, { at: now, actorId, fromStatus: c.status, toStatus: to, note }],
    });
}
/** The review queue: cases still needing a decision, oldest first. */
export function reviewQueue(cases) {
    return cases
        .filter((c) => c.status === 'OPEN' || c.status === 'UNDER_REVIEW' || c.status === 'APPEALED')
        .slice()
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}
//# sourceMappingURL=cases.js.map